import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { bareEnum, isActiveVersion, versionNameOf } from './types';

/**
 * The version list (SDD-016 D-V10): grouped by MAJOR, minors nested below in NUMERIC order, newest
 * major first; DRAFT versions — which have no number yet — in a group of their own at the top.
 *
 * Numeric, never lexical: as strings `1.10` sorts before `1.2`, and a list that did that would put
 * the newest revision in the middle of its major.
 */

export type VersionLifecycle = 'DRAFT' | 'PUBLISHED' | 'ACTIVE' | 'RETIRED' | 'DISCARDED';

/** A `major.minor` pair; `undefined` for a name that is not one (a draft id). */
export function versionNumberOf(name: string): { readonly major: number; readonly minor: number } | undefined {
  const match = /^([1-9][0-9]*)\.(0|[1-9][0-9]*)$/.exec(name);
  if (match === null) return undefined;
  return { major: Number(match[1]), minor: Number(match[2]) };
}

/** Numeric order of two version names: negative when a precedes b. Non-numbers sort last. */
export function compareVersionNames(a: string, b: string): number {
  const x = versionNumberOf(a);
  const y = versionNumberOf(b);
  if (x === undefined || y === undefined) {
    return x === undefined ? (y === undefined ? a.localeCompare(b) : 1) : -1;
  }
  return x.major !== y.major ? x.major - y.major : x.minor - y.minor;
}

/**
 * The version's lifecycle. `versionStatus` when the service sent one; a service (or a mock) that
 * predates SDD-016 is read the old way: the asset status, ACTIVE when the generation points at it.
 */
export function lifecycleOf(
  version: ConfigApi.TarifVersion,
  generation?: ConfigApi.Generation,
): VersionLifecycle {
  const status = bareEnum(version.versionStatus, 'VERSION_STATUS_');
  if (status === 'DRAFT' || status === 'PUBLISHED' || status === 'ACTIVE' || status === 'RETIRED' || status === 'DISCARDED') {
    if (status === 'PUBLISHED' && isActiveVersion(version, generation)) return 'ACTIVE';
    if (status === 'ACTIVE' && generation !== undefined && !isActiveVersion(version, generation)) return 'PUBLISHED';
    return status;
  }
  if (bareEnum(version.status, 'ASSET_STATUS_') === 'DRAFT') return 'DRAFT';
  return isActiveVersion(version, generation) ? 'ACTIVE' : 'PUBLISHED';
}

export interface VersionGroup {
  /** The major, or `undefined` for the drafts group. */
  readonly major: number | undefined;
  readonly versions: readonly ConfigApi.TarifVersion[];
}

export function groupVersionsByMajor(versions: readonly ConfigApi.TarifVersion[]): VersionGroup[] {
  const drafts: ConfigApi.TarifVersion[] = [];
  const majors = new Map<number, ConfigApi.TarifVersion[]>();
  for (const version of versions) {
    const number = versionNumberOf(versionNameOf(version));
    if (number === undefined) {
      drafts.push(version);
      continue;
    }
    const list = majors.get(number.major) ?? [];
    list.push(version);
    majors.set(number.major, list);
  }
  const groups: VersionGroup[] = [...majors.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([major, list]) => ({
      major,
      versions: [...list].sort((a, b) => compareVersionNames(versionNameOf(a), versionNameOf(b))),
    }));
  return drafts.length === 0 ? groups : [{ major: undefined, versions: drafts }, ...groups];
}

/** How many validation issues a draft still carries. */
export const issueCountOf = (version: ConfigApi.TarifVersion): number => version.validationIssues?.length ?? 0;
